import { DetailGroup } from 'components/UI/Responsive/DetailList';
import { OrderIntentionOriginEnum } from 'enums/orderIntentionOrigin.enum';
import { OrderIntentionStatusEnum } from 'enums/orderIntentionStatus.enum';
import { isNil } from 'lodash';
import { OrderIntention } from 'model/OrderIntention';
import moment from 'moment';
import { formatMoney } from 'screens/MyOrders/Detail/buildMyOrderDetailGroups';
import { dateUtils } from 'utils/date';
import { isShippingFeeBranch } from '../List/orderIntentionRules';

const emptyValue = '-';

const textOrDash = (value?: string | number | null): string =>
  isNil(value) || value === '' ? emptyValue : String(value);

const formatDateTime = (value?: string) =>
  value ? moment(value).format('DD/MM/YYYY - HH:mm') : emptyValue;

const ORIGIN_LABELS: Record<string, string> = {
  [OrderIntentionOriginEnum.ORDER_INTENTION]: 'Intenção de compra (IC)',
  [OrderIntentionOriginEnum.DIRECT_SALE]: 'Venda direta (VD)',
};

export const describeOrigin = (origin: string) =>
  ORIGIN_LABELS[origin] ?? textOrDash(origin);

export const describeDeliveryDate = (order: OrderIntention) =>
  order.orderNumber
    ? dateUtils.formatDate(order.deliveryDate, 'DD/MM/YYYY HH:mm')
    : dateUtils.formatDate(order.deliveryDate, 'DD/MM/YYYY');

export const describeProduct = (order: OrderIntention) =>
  order.conceptName
    ? `${order.productDescription} ${order.conceptName.toUpperCase()}`
    : order.productDescription;

export const describeOrderReference = (order: OrderIntention) =>
  order.orderNumber ? `Pedido ${order.orderNumber}` : `Ordem ${order.id}`;

export const unitTotal = (order: OrderIntention) =>
  order.unitPrice + order.shippingFee;

export const orderTotal = (order: OrderIntention, branch?: string) =>
  order.totalPrice +
  (isShippingFeeBranch(branch) ? order.shippingFee : 0) *
    order.quantityPacking *
    order.quantityUnit;

export const buildOrderIntentionDetailGroups = (
  order: OrderIntention,
  branch?: string,
): DetailGroup[] => [
  {
    name: 'Identificação',
    rows: [
      { label: 'Status', value: textOrDash(order.statusDescription) },
      ...(order.status === OrderIntentionStatusEnum.ERRO && order.message
        ? [{ label: 'Mensagem', value: order.message }]
        : []),
      {
        label: 'Chave',
        value: `${order.customerCode} - ${order.customerName}`,
      },
      { label: 'Pedido', value: textOrDash(order.orderNumber || null) },
      {
        label: 'Ordem de compra',
        value: order.orderNumber ? emptyValue : textOrDash(order.id),
      },
      { label: 'Origem', value: describeOrigin(order.origin) },
      {
        label: 'Produtor',
        value: order.siteName
          ? `${order.siteCode} - ${order.siteName}`
          : emptyValue,
      },
    ],
  },
  {
    name: 'Datas',
    rows: [
      { label: 'Data de entrega', value: describeDeliveryDate(order) },
      {
        label: 'Data comercial',
        value: dateUtils.formatDate(order.commercialDate, 'DD/MM/YYYY'),
      },
      {
        label: 'Data da publicação',
        value: formatDateTime(order.creationDate),
      },
      {
        label: 'Data de alteração/cancelamento',
        value:
          order.canceledBy && order.modificatioDate
            ? formatDateTime(order.modificatioDate)
            : emptyValue,
      },
    ],
  },
  {
    name: 'Produto',
    rows: [
      { label: 'Produto', value: describeProduct(order) },
      { label: 'Código do produto', value: textOrDash(order.productCode) },
      { label: 'Qld', value: textOrDash(order.qualityCode) },
      {
        label: 'Embalagem',
        value: `${order.packingCode} - ${order.packingDescription}`,
      },
      {
        label: 'QE x QpE',
        value: `${order.quantityPacking} x ${order.quantityUnit}`,
      },
      { label: 'QE atendida', value: textOrDash(order.quantityAttended) },
    ],
  },
  {
    name: 'Valores',
    rows: [
      { label: 'Valor unitário', value: formatMoney(unitTotal(order)) },
      { label: 'Valor total', value: formatMoney(orderTotal(order, branch)) },
      ...(isShippingFeeBranch(branch)
        ? [
            { label: 'Frete unitário', value: formatMoney(order.shippingFee) },
            {
              label: 'Frete total',
              value: formatMoney(
                order.shippingFee * order.quantityPacking * order.quantityUnit,
              ),
            },
          ]
        : []),
    ],
  },
  {
    name: 'Outros',
    rows: [
      {
        label: 'Natureza op.',
        value: textOrDash(order.natureOperationDescription),
      },
      { label: 'Retirada', value: textOrDash(order.removeLocation) },
      { label: 'Observação', value: textOrDash(order.observation) },
      { label: 'Justificativa', value: textOrDash(order.justification) },
      { label: 'Publicado por', value: textOrDash(order.publishedBy) },
      { label: 'Respondido por', value: textOrDash(order.answeredBy) },
      {
        label: 'Alterado/Cancelado por',
        value: textOrDash(order.canceledBy),
      },
      {
        label: 'Motivo de alteração/cancelamento',
        value: textOrDash(order.cancelationJustification),
      },
    ],
  },
];
